import { formatLengthToArchitecturalString } from '@paragon/client-tools';
import { RoofPlane } from '@paragon/contract-library';
import { angleGreaterThanOrEqual, convertDegreesToRadians, degreesToSlope } from '@paragon/geometry-library';
import {
  calculateTotalHeelHeight,
  editHeelSettings,
  PlaneWithCalculatedGeometry,
  RoofPlaneEditInstructions,
} from '@paragon/layout';
import { assertExhaustiveSwitch } from '@paragon/utilities';

// TODO: Avery is investigating the validity of these hard-coded values
const FASCIA_THICKNESS = 0;
const DECK_ANGLE_THICKNESS = 0;

type AdjustablePlane = PlaneWithCalculatedGeometry<RoofPlane>;
type AdjustmentResults = RoofPlaneEditInstructions[] | { error: string };

export function matchFasciaHeights(
  referencePlane: AdjustablePlane,
  planesToAdjust: AdjustablePlane[],
  options: { mode: 'overhang' | 'heel-height' },
): AdjustmentResults {
  const adjustToMatch = getAdjustmentFunction(options.mode);

  const referenceHeelHeight = getHeelHeight(referencePlane);
  if (referenceHeelHeight == null) return nullHeelHeightError();

  const referenceMatchPointElevation = getMatchPointElevation(referencePlane, referenceHeelHeight);

  const adjustments = planesToAdjust.map((plane) => adjustToMatch(plane, referenceMatchPointElevation));
  const errors = adjustments.filter((result): result is { error: string } => 'error' in result);

  return errors.length > 0 ? errors[0] : (adjustments as RoofPlaneEditInstructions[]);
}

function getAdjustmentFunction(mode: 'overhang' | 'heel-height') {
  switch (mode) {
    case 'overhang':
      return adjustOverhangToMatchFasciaHeight;
    case 'heel-height':
      return adjustHeelSettingsToMatchFasciaHeight;
    default:
      assertExhaustiveSwitch(mode);
  }
}

function adjustHeelSettingsToMatchFasciaHeight(plane: AdjustablePlane, referenceMatchPointElevation: number) {
  const heelHeight = getHeelHeight(plane);
  if (heelHeight == null) return nullHeelHeightError();

  if (angleGreaterThanOrEqual(plane.slopeInDegrees, 90)) return noVerticalPlanesError();
  const pitchAngle = convertDegreesToRadians(plane.slopeInDegrees);
  const topChordAngleDistance = plane.topChordWidth / Math.cos(pitchAngle);

  const matchPointElevation = getMatchPointElevation(plane, heelHeight);

  const minHeel = topChordAngleDistance + 0.25;
  let heelDiff = heelHeight - minHeel;

  const minMatchPointElevation = matchPointElevation - heelDiff;
  if (referenceMatchPointElevation < minMatchPointElevation) {
    return minMatchPointError(minMatchPointElevation);
  }

  heelDiff = referenceMatchPointElevation - matchPointElevation;

  const adjustedHeelHeight = heelHeight + heelDiff;
  const adjustedButtCut = adjustedHeelHeight - topChordAngleDistance;

  return {
    guid: plane.guid,
    editedProperties: {
      heelSettings: editHeelSettings(plane.heelSettings, adjustedHeelHeight, adjustedButtCut),
    },
  };
}

function adjustOverhangToMatchFasciaHeight(plane: AdjustablePlane, referenceMatchPointElevation: number) {
  const heelHeight = getHeelHeight(plane);
  if (heelHeight == null) return nullHeelHeightError();
  if (angleGreaterThanOrEqual(plane.slopeInDegrees, 90)) return noVerticalPlanesError();

  const pitchSlope = degreesToSlope(plane.slopeInDegrees);
  const matchPointElevation = getMatchPointElevation(plane, heelHeight);

  const maxMatchPointElevation =
    plane.calculatedGeometry.elevation +
    heelHeight +
    DECK_ANGLE_THICKNESS -
    (plane.subfasciaThickness + FASCIA_THICKNESS) * (pitchSlope / 12);

  if (referenceMatchPointElevation <= maxMatchPointElevation) {
    const heightDiff = matchPointElevation - referenceMatchPointElevation;
    const run = heightDiff * (12.0 / pitchSlope);

    const adjustedOverhangDistance = plane.overhang + run;

    return {
      guid: plane.guid,
      editedProperties: {
        overhang: adjustedOverhangDistance,
      },
    };
  }

  return maxMatchPointError(maxMatchPointElevation);
}

function getHeelHeight(plane: AdjustablePlane) {
  return calculateTotalHeelHeight(plane.heelSettings, plane.topChordWidth, plane.slopeInDegrees);
}

function getMatchPointElevation(plane: AdjustablePlane, heelHeight: number): number {
  const pitch = degreesToSlope(plane.slopeInDegrees);
  const offsetFromTopHeel = (plane.overhang + plane.subfasciaThickness + FASCIA_THICKNESS) * (pitch / 12);

  return plane.calculatedGeometry.elevation + heelHeight + DECK_ANGLE_THICKNESS - offsetFromTopHeel;
}

function nullHeelHeightError() {
  return { error: 'Heel height could not be determined for a plane to adjust' };
}

function minMatchPointError(minElevation: number) {
  return {
    error: `A plane could not be adjusted—its minimum match point elevation is ${formatLengthToArchitecturalString(
      minElevation,
    )}`,
  };
}

function maxMatchPointError(maxElevation: number) {
  return {
    error: `A plane could not be adjusted—its maximum match point elevation is ${formatLengthToArchitecturalString(
      maxElevation,
    )}.`,
  };
}

function noVerticalPlanesError() {
  return { error: "Vertical roof planes can't be adjusted to match fascia height." };
}
